import React from "react";
import {ICardStrategy} from "../card.strategy.interface";
import {
    NuveiCardPaymentMethodsTabContentComponent
} from "../../../../../../../../pages/booking-flow/payment/card/nuvei/nuvei-card-payment-methods-tab-content.component";
import {BookingModel} from "../../../../../booking.model";
import {IServiceFactory} from "../../../../../../../service-factory.interface";
import {makeObservable, observable, runInAction} from "mobx";
import {INuveiCardStrategyViewModel} from "./nuvei-card-strategy.view-model.interface";
import {Price} from "../../../../../../../currency/price";
import {Lazy} from "../../../../../../../../utils/lazy";
import {NuveiCardFieldModel} from "./nuvei-card-field.model";
import {NuveiCardHolderFieldModel} from "./nuvei-card-holder-field.model";
import {INuveiCardFieldModel} from "./nuvei-card-field.view-model.interface";
import {NullableString} from "../../../../../../../../types/nullable-types";
import {ValidationResultEnum} from "../../../../../../../../types/validation-result.enum";
import {ICardNetworkViewModel} from "../../networks/card-network.view-model.interface";

interface ICreateCardFieldOptions {
    fieldType: SafeCharge.FieldType;
}

export class NuveiCardStrategy implements ICardStrategy, INuveiCardStrategyViewModel {
    constructor(private readonly booking: BookingModel) {
        const nuveiConfigs =  this.nuveiConfigs;
        this._safeCharge = SafeCharge({
            env: nuveiConfigs.environment,
            merchantId: nuveiConfigs.merchantId,
            merchantSiteId: nuveiConfigs.merchantSiteId,
            locale: this.services.language.currentLanguageIso2,
            //blockCards: UNSUPPORTED_NUVEI_CARD_TYPES.map(cardType => [cardType])
        });


        this._fields = this._safeCharge.fields({
            fonts: [{
                cssUrl: 'https://fonts.googleapis.com/css2?family=Anek+Latin:wght@100..800&display=swap'
            }],
            locale: this.services.language.currentLanguageIso2,
        });

        makeObservable<this, '_errorMessage'>(this, {
            _errorMessage: observable.ref,
        })
    }

    private readonly _safeCharge: SafeCharge.Instance;
    private readonly _fields: SafeCharge.Fields;

    private get services(): IServiceFactory {
        return this.booking.services;
    }

    private get nuveiConfigs() {
        return this.services.configuration.data.pspConfigs.Nuvei;
    }

    get isDisabled(): boolean {
        return this.booking.paymentHandler.paymentMethodsDisabled;
    }

    private _cardHolderField: Lazy<NuveiCardHolderFieldModel> = new Lazy<NuveiCardHolderFieldModel>(() => {
        return new NuveiCardHolderFieldModel(this.services);
    });

    get cardHolderField(): NuveiCardHolderFieldModel {
        return this._cardHolderField.value;
    }

    private _cardNumberField: Lazy<NuveiCardFieldModel> = new Lazy<NuveiCardFieldModel>(() => {
        return this._createCardField({
            fieldType: 'ccNumber',
        });
    });

    get cardNumberField(): NuveiCardFieldModel{
        return this._cardNumberField.value;
    }


    private _expirationDateField: Lazy<NuveiCardFieldModel> = new Lazy<NuveiCardFieldModel>(() => {
        return this._createCardField({
            fieldType: 'ccExpiration',
        });
    });

    get expirationDateField(): NuveiCardFieldModel {
        return this._expirationDateField.value;

    }

    private _securityCodeField: Lazy<NuveiCardFieldModel> = new Lazy<NuveiCardFieldModel>(() => {
        return this._createCardField({
            fieldType: 'ccCvc',
        });
    });

    get securityCodeField(): NuveiCardFieldModel {
        return this._securityCodeField.value;
    }

    get allFields(): INuveiCardFieldModel[] {
        return [
            this.cardHolderField,
            this.cardNumberField,
            this.expirationDateField,
            this.securityCodeField
        ];
    }

    get amount(): Price {
        return this.booking.balanceDue;
    }

    get supportedCardNetworks(): ICardNetworkViewModel[] {
        return this.booking.paymentHandler.cardPaymentMethod.networks;
    }

    renderTabContent(): React.ReactElement {
        return (
            <NuveiCardPaymentMethodsTabContentComponent nuveiStrategy={this}/>
        )
    }

    private _errorMessage: NullableString = null;
    get errorMessage(): NullableString {
        if(this.isDisabled) {
            return null;
        }

        if(!this.allFields.all(f => f.isErrorsValidationActive)) {
            return null;
        }
        return this._errorMessage;
    }

    set errorMessage(value: NullableString) {
        runInAction(() => {
            this._errorMessage = value;
        });
    }

    private _createFieldsStyle(): SafeCharge.FieldStyle {
        return {
            base: {
                color: "#102930",
                fontFamily: 'Anek Latin',
                fontSize: "16pt",
                '::placeholder': {
                    color: "#92949C"
                }
            },
            invalid: {
                //color: "#EB445A"
                color: "#102930",
            },
            valid: {
                color: "#102930"
            }
        }
    }

    private _createCardField(options: ICreateCardFieldOptions): NuveiCardFieldModel {

        const field = this._fields.create(options.fieldType, {
            style: this._createFieldsStyle()
        });

        return new NuveiCardFieldModel(options.fieldType, field, this.services);
    }

    private _validateFields(): ValidationResultEnum {
        this.allFields.forEach(f => f.activateValidationErrors())
        const firstError = this.allFields.find(f => Boolean(f.errorMessage));
        if(firstError) {
            firstError.focus();
            return ValidationResultEnum.Failure;
        }
        return ValidationResultEnum.Success;
    }


    async startPayment(): Promise<void> {

        this.errorMessage = null;

        if(ValidationResultEnum.Success !== this._validateFields()) {
            return;
        }

        const response = await this.booking.paymentHandler.startBookingPayment({
            paymentMethodCode: this.booking.paymentHandler.cardPaymentMethod.code
        });

        if(!response.pspOrderToken) {
            return;
        }

        await this.services.loadingIndicator.execute({
            action: async () => {
                const createPaymentResult = await this._createPayment(response.pspOrderToken!);
                this.errorMessage = createPaymentResult.errorDescription;
            }
        });

        await this.booking.paymentHandler.finalizePspOrder();

    }

    private _createPayment(pspOrderToken: string): Promise<SafeCharge.PaymentResponse> {
        return new Promise<SafeCharge.PaymentResponse>((resolve) => {
            this._safeCharge.createPayment({
                sessionToken: pspOrderToken,
                merchantId: this.nuveiConfigs.merchantId,
                merchantSiteId: this.nuveiConfigs.merchantSiteId,
                paymentOption: this.cardNumberField.safeChargeField,
                cardHolderName: this.cardHolderField.cardHolderName

            }, (response) => {
                resolve(response);
            });
        })
    }

}